import { ExternalLink, Mail } from 'lucide-react'

import { EmptyState, ErrorState, SkeletonCards } from '@/shared/components/data'
import { useT } from '@/shared/i18n'

import { usePortalCompany } from './api'
import type { PortalContact, PortalTechStackItem } from './types'

/**
 * `/portal/company` — la ficha de su empresa.
 *
 * Lo que llega es lo que el backend enumeró a mano: nombre, contactos sin notas
 * internas y los ítems del stack marcados como visibles. No hay canal de Slack,
 * no hay responsable técnico, no hay notas del equipo — y no porque estén
 * ocultos en la UI, sino porque el schema del portal no los tiene.
 */
export function PortalCompanyPage() {
  const t = useT()
  const query = usePortalCompany()

  if (query.isPending) return <SkeletonCards count={3} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }
  if (!query.data) return <ErrorState title={t('portal.company.loadError')} />

  const { company, contacts, tech_stack: stack } = query.data

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-lg text-text-1">{company.name}</h1>
        <p className="text-xs text-text-2">{t('portal.company.subtitle')}</p>
      </header>

      <section className="flex flex-col gap-2">
        <h2 className="text-2xs tracking-wide text-text-3 uppercase">
          {t('portal.company.contacts')}
        </h2>
        <Contacts contacts={contacts} />
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-2xs tracking-wide text-text-3 uppercase">{t('portal.company.tech')}</h2>
        <TechStack stack={stack} />
      </section>
    </div>
  )
}

function Contacts({ contacts }: { contacts: readonly PortalContact[] }) {
  const t = useT()

  if (contacts.length === 0) {
    return (
      <EmptyState
        title={t('portal.company.contactsEmptyTitle')}
        description={t('portal.company.contactsEmptyDescription')}
      />
    )
  }

  return (
    <ul
      aria-label={t('portal.company.contacts')}
      className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
    >
      {contacts.map((contact) => (
        <li
          key={contact.id}
          className="hairline-b flex flex-wrap items-center gap-3 px-4 py-3 last:border-b-0"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm text-text-1">{contact.name}</span>
            {contact.role_title ? (
              <span className="text-2xs text-text-3">{contact.role_title}</span>
            ) : null}
          </span>

          <a
            href={`mailto:${contact.email}`}
            className="num inline-flex items-center gap-1.5 text-xs text-text-2 hover:text-text-1"
          >
            <Mail size={13} aria-hidden="true" />
            {contact.email}
          </a>
        </li>
      ))}
    </ul>
  )
}

function TechStack({ stack }: { stack: readonly PortalTechStackItem[] }) {
  const t = useT()

  if (stack.length === 0) {
    return (
      <EmptyState
        title={t('portal.company.techEmptyTitle')}
        description={t('portal.company.techEmptyDescription')}
      />
    )
  }

  // Agrupado por categoría y en el orden en que vino: el backend ya decide qué
  // se publica y en qué orden, y reordenar acá sería una segunda opinión.
  //
  // La categoría y el nombre del ítem los escribió el equipo en la ficha de la
  // empresa: son texto de usuario y viajan tal cual, sin pasar por el catálogo.
  const groups = new Map<string, PortalTechStackItem[]>()
  for (const item of stack) {
    const bucket = groups.get(item.category) ?? []
    bucket.push(item)
    groups.set(item.category, bucket)
  }

  return (
    <div className="flex flex-col gap-3">
      {[...groups.entries()].map(([category, items]) => (
        <section key={category} className="flex flex-col gap-1.5">
          <h3 className="text-2xs text-text-3">{category}</h3>
          <ul aria-label={category} className="flex flex-wrap gap-2">
            {items.map((item) => (
              <li
                key={`${category}:${item.name}:${item.version ?? ''}`}
                className="hairline flex items-center gap-2 rounded-[var(--radius-control)] bg-surface-1 px-2.5 py-1.5"
              >
                <span className="num text-xs text-text-1">{item.name}</span>
                {item.version ? (
                  <span className="num text-2xs text-text-3">{item.version}</span>
                ) : null}
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t('portal.company.openTech', { nombre: item.name })}
                    className="text-text-3 hover:text-text-1"
                  >
                    <ExternalLink size={12} aria-hidden="true" />
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
